import { z } from 'zod';

import { OpsVasKindSchema } from './ops-cases.js';

/**
 * Screenshots the seller attaches on a long-haul lead (lead detail page,
 * Screenshots tab after Call Analysis), one or more per booking item of the
 * converted itinerary - the same rows as the ops booking card. Images only
 * (PNG, JPG or WebP), sent as base64 JSON like the ops documents upload.
 */

export const LEAD_SCREENSHOT_MAX_BYTES = 8 * 1024 * 1024;
/** Files per upload call (one row, several images). */
export const LEAD_SCREENSHOT_MAX_FILES = 10;
export const LEAD_SCREENSHOT_NOTE_MAX = 500;

export const LeadScreenshotUploadInputSchema = z.object({
  /** The booking item (itinerary component) these screenshots are for. */
  componentId: z.string().uuid(),
  files: z
    .array(
      z.object({
        fileName: z.string().trim().min(1).max(200),
        /** The file bytes, base64 (no `data:` prefix). */
        contentBase64: z.string().min(1),
      }),
    )
    .min(1)
    .max(LEAD_SCREENSHOT_MAX_FILES),
  /** A word for ops about these screenshots (shown next to them on the ops case). */
  note: z.string().trim().max(LEAD_SCREENSHOT_NOTE_MAX).nullable().optional(),
});
export type LeadScreenshotUploadInput = z.infer<typeof LeadScreenshotUploadInputSchema>;

export const LeadScreenshotSchema = z.object({
  id: z.string().uuid(),
  leadId: z.string().uuid(),
  componentId: z.string().uuid().nullable(),
  fileName: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int(),
  uploadedById: z.string().uuid(),
  uploadedByName: z.string(),
  note: z.string().nullable(),
  createdAt: z.coerce.date(),
  /** The viewer may remove it (uploader, ADMIN or SALES_LEAD). */
  canDelete: z.boolean(),
});
export type LeadScreenshot = z.infer<typeof LeadScreenshotSchema>;

/** One booking item row: just its name (a transfer's route). */
export const LeadScreenshotItemSchema = z.object({
  componentId: z.string().uuid(),
  kind: z.enum(['FLIGHT', 'HOTEL', 'TRANSFER', 'ACTIVITY', 'VAS', 'CRUISE']),
  vasKind: OpsVasKindSchema.nullable().catch(null),
  name: z.string(),
});
export type LeadScreenshotItem = z.infer<typeof LeadScreenshotItemSchema>;

export const LeadScreenshotsViewSchema = z.object({
  /** The itinerary the rows come from; null = the lead has none converted yet. */
  itineraryId: z.string().uuid().nullable(),
  items: z.array(LeadScreenshotItemSchema),
  screenshots: z.array(LeadScreenshotSchema),
});
export type LeadScreenshotsView = z.infer<typeof LeadScreenshotsViewSchema>;

/** A seller screenshot as ops sees it on the converted lead's Documents block (read-only). */
export const OpsSellerScreenshotSchema = z.object({
  id: z.string().uuid(),
  componentId: z.string().uuid().nullable(),
  /** The booking item it was uploaded against. */
  componentTitle: z.string().nullable(),
  fileName: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int(),
  uploadedByName: z.string(),
  /** The seller's note from the upload. */
  note: z.string().nullable(),
  createdAt: z.coerce.date(),
});
export type OpsSellerScreenshot = z.infer<typeof OpsSellerScreenshotSchema>;
export const OpsSellerScreenshotListSchema = z.array(OpsSellerScreenshotSchema);
